import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
    Menu,
    X,
    FileText,
    Droplet,
    History,
    LogOut,
    ChevronDown,
    Fingerprint,
    Loader2,
    MapPin,
    Activity,
    Info,
    XCircle,
    Settings,
    AlertTriangle,
    Handshake,
    LogOut as LogOutIcon
} from 'lucide-react';
import config from '../config';

export default function Inicio() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [isBioSupported, setIsBioSupported] = useState(false);
    const [isBioEnabled, setIsBioEnabled] = useState(false);
    const [showBioModal, setShowBioModal] = useState(false);
    const [bioLoading, setBioLoading] = useState(false);
    const [bioError, setBioError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const currentYear = new Date().getFullYear();

    const base64URLToBuffer = (base64url) => {
        if (!base64url) return new Uint8Array(0);
        let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        if (pad) base64 += '='.repeat(4 - pad);
        const binStr = window.atob(base64);
        return Uint8Array.from(binStr, c => c.charCodeAt(0));
    };

    const bufferToBase64URL = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let str = "";
        for (const charCode of bytes) str += String.fromCharCode(charCode);
        return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    };

    const getActiveTab = () => {
        if (location.pathname.includes('consumos')) return 'consumos';
        if (location.pathname.includes('pagos')) return 'pagos';
        if (location.pathname.includes('convenios')) return 'convenios';
        return 'boletas';
    };

    const activeTab = getActiveTab();

    useEffect(() => {
        const checkSession = () => {
            if (sessionExpired) return;
            const expiry = sessionStorage.getItem('session_expiry');
            const token = sessionStorage.getItem('token');
            if (token && expiry) {
                const now = Date.now();
                if (now > parseInt(expiry)) {
                    handleSessionExpired();
                }
            } else if (!token && !location.pathname.includes('login')) {
                navigate('/login');
            }
        };
        const interval = setInterval(checkSession, 1000);
        return () => clearInterval(interval);
    }, [navigate, location, sessionExpired]);

    const handleSessionExpired = () => {
        setSessionExpired(true);
        sessionStorage.clear();
    };

    const closeSessionModal = () => {
        setSessionExpired(false);
        navigate('/login');
    };

    useEffect(() => {
        const checkBiometry = async () => {
            if (window.PublicKeyCredential) {
                try {
                    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                    setIsBioSupported(available);
                    const token = sessionStorage.getItem('token');
                    const deviceId = localStorage.getItem('biometric_device_id');
                    if (token && deviceId) {
                        const res = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-biometrics-status?device_id=${deviceId}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (res.ok) {
                            const data = await res.json();
                            setIsBioEnabled(data.enabled);
                        }
                    } else {
                        const enabled = localStorage.getItem('biometric_enabled') === 'true';
                        setIsBioEnabled(enabled);
                    }
                    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                    const wasPrompted = localStorage.getItem('biometric_prompt_shown') === 'true';
                    if (available && isMobile && !isBioEnabled && !wasPrompted) {
                        setShowBioModal(true);
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        };
        checkBiometry();
    }, [isBioEnabled]);

    const fetchProfile = async () => {
        const token = sessionStorage.getItem('token');
        if (!token) {
            const mockData = [{
                nro_cliente: 12345, nom_cliente: "Juan", apel_paterno: "Pérez",
                rut_cliente: "12345678", dig_cliente: "9", up_direccion: "Calle Falsa 123",
                sector: "Sector Central", rutas: "Ruta 01", tipo_cliente: "Residencial",
                medidor: "ABC-987", estado_servicio: "Activo", desc_tipo_estructura_medidor: "Electrónico"
            }];
            setProperties(mockData);
            setSelectedProperty(mockData[0]);
            setLoading(false);
            return;
        }
        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-details`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setProperties(data);
                if (data.length > 0) setSelectedProperty(data[0]);
            } else if (response.status === 401) {
                handleSessionExpired();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const handleEnableBiometrics = async () => {
        setBioLoading(true);
        setBioError('');
        const token = sessionStorage.getItem('token');
        try {
            const startRes = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-biometrics-register-start`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ eid: config.aprEid })
            });
            if (!startRes.ok) throw new Error("No se pudo iniciar el proceso.");
            const data = await startRes.json();
            const publicKeyOptions = {
                challenge: base64URLToBuffer(data.challenge),
                rp: { name: config.aprName, id: window.location.hostname },
                user: { id: base64URLToBuffer(data.user_id), name: data.user_name, displayName: data.user_name },
                pubKeyCredParams: [{ alg: -7, type: "public-key" }, { alg: -257, type: "public-key" }],
                authenticatorSelection: {
                    authenticatorAttachment: "platform",
                    userVerification: "required",
                    residentKey: "required",
                    requireResidentKey: true
                },
                timeout: 60000
            };
            const credential = await window.navigator.credentials.create({ publicKey: publicKeyOptions });
            if (!credential) throw new Error("Operación cancelada.");
            const publicKeyBuffer = credential.response.getPublicKey();
            const payload = { id: credential.id, response: { publicKey: bufferToBase64URL(publicKeyBuffer) } };
            const enableRes = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-biometrics-enable`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (enableRes.ok) {
                localStorage.setItem('biometric_enabled', 'true');
                localStorage.setItem('biometric_device_id', payload.id);
                setIsBioEnabled(true);
                setShowBioModal(false);
            } else {
                const errData = await enableRes.json();
                throw new Error(errData.message || "Error al habilitar.");
            }
        } catch (error) {
            setBioError(error.name === 'NotAllowedError' ? 'Operación cancelada.' : error.message || 'Error técnico.');
        } finally {
            setBioLoading(false);
            localStorage.setItem('biometric_prompt_shown', 'true');
        }
    };

    const handleLogout = () => {
        sessionStorage.clear();
        navigate('/login');
    };

    const handlePropertyChange = (e) => {
        const nro = parseInt(e.target.value);
        const prop = properties.find(p => p.nro_cliente === nro);
        setSelectedProperty(prop);
    };

    const handleNavClick = (path) => {
        navigate(path);
        setSidebarOpen(false);
    };

    const DetailItem = ({ label, value, icon: Icon }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase font-black text-sky-200/70 flex items-center gap-1">
                {Icon && <Icon size={10} />} {label}
            </span>
            <span className="text-xs font-bold text-white truncate">{value || '---'}</span>
        </div>
    );

    const ProfileAndSelector = () => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/10">
                <div className="h-10 w-10 rounded-full bg-sky-500 flex items-center justify-center text-white font-bold border-2 border-white shadow-sm uppercase text-xs">
                    {selectedProperty ? `${selectedProperty.nom_cliente?.charAt(0)}${selectedProperty.apel_paterno?.charAt(0)}` : "..."}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate uppercase">{selectedProperty ? `${selectedProperty.nom_cliente} ${selectedProperty.apel_paterno}` : "Cargando..."}</p>
                    <p className="text-xs text-sky-200">{selectedProperty ? `RUT: ${selectedProperty.rut_cliente}-${selectedProperty.dig_cliente}` : "---"}</p>
                </div>
            </div>
            <div className="relative">
                <label className="text-[10px] uppercase font-black text-sky-200 ml-1 mb-1 block">Seleccionar Servicio</label>
                <select value={selectedProperty?.nro_cliente || ''} onChange={handlePropertyChange} className="w-full bg-sky-800/50 border border-white/20 rounded-xl px-4 py-3 text-sm font-bold appearance-none focus:outline-none transition-all cursor-pointer text-white">
                    {properties.map(prop => (
                        <option key={prop.id || prop.nro_cliente} value={prop.nro_cliente} className="bg-sky-900 text-white">#{prop.nro_cliente} - {prop.up_direccion}</option>
                    ))}
                </select>
                <div className="absolute right-4 bottom-3.5 pointer-events-none text-sky-200"><ChevronDown size={16} /></div>
            </div>
            {selectedProperty && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-md space-y-4 animate-in fade-in slide-in-from-top-2 duration-500">
                    <div className="grid grid-cols-2 gap-y-4 gap-x-2">
                        <DetailItem label="Sector" value={selectedProperty.sector} icon={MapPin} />
                        <DetailItem label="Ruta" value={selectedProperty.rutas} icon={Settings} />
                        <DetailItem label="Tipo Cliente" value={selectedProperty.tipo_cliente} icon={Info} />
                        <DetailItem label="N° Medidor" value={selectedProperty.medidor} icon={Settings} />
                        <DetailItem label="Estado" value={selectedProperty.estado_servicio} icon={Activity} />
                        <DetailItem label="Tipo Medidor" value={selectedProperty.desc_tipo_estructura_medidor} icon={Settings} />
                    </div>
                </div>
            )}
        </div>
    );

    const NavLinks = () => (
        <nav className="flex flex-col gap-2 w-full">
            <button onClick={() => handleNavClick('boletas')} className={`w-full flex items-center gap-3 px-6 py-4 rounded-xl transition-all font-bold text-left ${activeTab === 'boletas' ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/10' : 'text-sky-100 hover:bg-white/10 hover:text-white'}`}><FileText size={22} /> Mis Boletas</button>
            <button onClick={() => handleNavClick('consumos')} className={`w-full flex items-center gap-3 px-6 py-4 rounded-xl transition-all font-bold text-left ${activeTab === 'consumos' ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/10' : 'text-sky-100 hover:bg-white/10 hover:text-white'}`}><Droplet size={22} /> Mis Consumos</button>
            <button onClick={() => handleNavClick('pagos')} className={`w-full flex items-center gap-3 px-6 py-4 rounded-xl transition-all font-bold text-left ${activeTab === 'pagos' ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/10' : 'text-sky-100 hover:bg-white/10 hover:text-white'}`}><History size={22} /> Pagos Realizados</button>
            <button onClick={() => handleNavClick('convenios')} className={`w-full flex items-center gap-3 px-6 py-4 rounded-xl transition-all font-bold text-left ${activeTab === 'convenios' ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/10' : 'text-sky-100 hover:bg-white/10 hover:text-white'}`}><Handshake size={22} /> Mis Convenios</button>
        </nav>
    );

    return (
        <div className="h-screen w-full flex bg-slate-50 font-sans text-slate-600 overflow-hidden">
            {sessionExpired && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300"></div>
                    <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
                        <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6"><AlertTriangle size={40} /></div>
                        <h3 className="text-2xl font-black text-slate-800 mb-2">Sesión Expirada</h3>
                        <p className="text-slate-500 mb-8 leading-relaxed">Por tu seguridad, la sesión se cierra automáticamente después de 15 minutos.</p>
                        <button onClick={closeSessionModal} className="w-full py-4 bg-slate-800 text-white rounded-2xl font-bold shadow-xl hover:bg-slate-900 transition-all active:scale-95">Volver al Ingreso</button>
                    </div>
                </div>
            )}
            {showLogoutModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowLogoutModal(false)}></div>
                    <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4"><LogOutIcon size={32} /></div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">¿Cerrar Sesión?</h3>
                        <p className="text-slate-500 text-sm mb-6 leading-relaxed">¿Estás seguro que deseas salir de tu cuenta? Tendrás que ingresar tus datos nuevamente.</p>
                        <div className="flex flex-col gap-3">
                            <button onClick={handleLogout} className="w-full py-4 bg-red-600 text-white rounded-xl font-bold shadow-lg hover:bg-red-700 transition-all active:scale-95">Sí, cerrar sesión</button>
                            <button onClick={() => setShowLogoutModal(false)} className="w-full py-3 text-slate-400 font-bold hover:text-slate-600 text-sm">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
            {showBioModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"></div>
                    <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
                        <div className="w-16 h-16 bg-sky-100 text-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-4"><Fingerprint size={32} /></div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">Habilitar Biometría</h3>
                        <p className="text-slate-500 text-sm mb-6 leading-relaxed">¿Deseas activar el acceso biométrico (huella o rostro)?</p>
                        {bioError && <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-600 text-xs font-bold text-left animate-in slide-in-from-top-1"><XCircle size={16} /> {bioError}</div>}
                        <div className="flex flex-col gap-3">
                            <button onClick={handleEnableBiometrics} disabled={bioLoading} className="w-full py-4 bg-sky-600 text-white rounded-xl font-bold shadow-lg hover:bg-sky-700 transition-all flex justify-center items-center gap-2">{bioLoading ? <Loader2 className="animate-spin" size={20} /> : "Sí, habilitar"}</button>
                            <button onClick={() => { setShowBioModal(false); setBioError(''); localStorage.setItem('biometric_prompt_shown', 'true'); }} className="w-full py-2 text-slate-400 font-bold hover:text-slate-600 text-sm">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
            <aside className="hidden lg:flex lg:w-[400px] xl:w-[420px] relative flex-col justify-between overflow-hidden bg-sky-600 shrink-0 shadow-2xl z-20">
                <div className="absolute inset-0"><img src="/background.avif" alt="Agua" className="w-full h-full object-cover opacity-50 mix-blend-overlay" /></div>
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent"></div>
                <div className="relative z-10 flex flex-col h-full p-8 text-white overflow-y-auto no-scrollbar">
                    <div className="mb-8">
                        <img className="image-invert-white" style={{height:'100px', margin: 'auto', marginBottom: '30px'}} src={config.aprLogo} alt={config.aprName} />
                        <ProfileAndSelector />
                    </div>
                    <NavLinks />
                    <div className="mt-8 space-y-4 flex flex-col">
                        {isBioSupported && !isBioEnabled && <button onClick={() => { setShowBioModal(true); setBioError(''); }} className="w-full flex items-center gap-3 px-6 py-4 rounded-xl transition-all font-bold text-left bg-emerald-500/20 text-emerald-100 border border-emerald-400/20 hover:bg-emerald-500/30 mb-4"><Fingerprint size={22} /> Activar Biometría</button>}
                        <button onClick={() => setShowLogoutModal(true)} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/30 text-sky-100 hover:bg-white/10 hover:text-white transition-colors text-sm font-bold backdrop-blur-sm"><LogOut size={18} /> Cerrar Sesión</button>
                        <div className="mt-8 flex flex-col items-center gap-2 pb-6">
                            <span className="text-[10px] font-bold text-sky-300/40 uppercase tracking-widest text-center">Powered by</span>
                            <img className="image-invert-white mb-2" alt={config.aprName} src="/logo.snap.png" style={{height: '40px'}} />
                            <div className="text-[10px] text-sky-300/60 text-center font-bold">© {currentYear} {config.aprName}</div>
                        </div>
                    </div>
                </div>
            </aside>
            <div className={`fixed inset-0 z-50 lg:hidden flex transition-opacity duration-300 ease-in-out ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
                <div className={`relative w-4/5 max-w-xs bg-sky-600 h-full shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 ease-in-out transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="absolute inset-0"><img src="/background.avif" className="w-full h-full object-cover opacity-50 mix-blend-overlay" alt="Fondo" /></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent"></div>
                    <div className="relative z-10 flex flex-col h-full p-6 text-white overflow-y-auto no-scrollbar">
                        <div className="flex flex-col items-center mb-8">
                            <button onClick={() => setSidebarOpen(false)} className="self-end p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors mb-2"><X size={20} /></button>
                            <img className="image-invert-white" style={{height:'100px', margin: 'auto', marginBottom: '20px'}} src={config.aprLogo} alt={config.aprName} />
                            <ProfileAndSelector />
                        </div>
                        <NavLinks />
                        <div className="mt-auto pt-8 flex flex-col items-center gap-4">
                            <button onClick={() => setShowLogoutModal(true)} className="w-full py-3 rounded-xl border border-white/30 text-sky-100 font-bold flex justify-center gap-2 transition-transform active:scale-95"><LogOut size={18} /> Salir</button>
                            <div className="flex flex-col items-center gap-2 pb-24">
                                <span className="text-[10px] font-bold text-sky-300/40 uppercase tracking-widest text-center">Powered by</span>
                                <img className="image-invert-white" alt={config.aprName} src="/logo.snap.png" style={{height: '30px'}} />
                                <div className="text-[10px] text-sky-300/60 text-center font-bold">© {currentYear} {config.aprName}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <main className="flex-1 relative flex flex-col h-screen overflow-hidden bg-slate-50 transition-colors duration-500">
                <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-slate-200 text-slate-700 shadow-sm z-10">
                    <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors"><Menu size={24} /></button>
                    <div className="flex flex-col items-center">
                        <span className="font-bold text-sky-700 text-sm uppercase truncate max-w-[150px]">{activeTab}</span>
                        <span className="text-[10px] font-bold text-slate-400">#{selectedProperty?.nro_cliente}</span>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center font-bold text-xs border border-sky-200 uppercase shrink-0">{selectedProperty ? `${selectedProperty.nom_cliente?.charAt(0)}${selectedProperty.apel_paterno?.charAt(0)}` : ".."}</div>
                </header>
                <div className="flex-1 overflow-y-auto p-4 lg:p-12">
                    {!loading && selectedProperty && <Outlet context={{ selectedProperty, allProperties: properties }} />}
                </div>
            </main>
        </div>
    );
}
